import { NavLink, Outlet, Navigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LayoutDashboard, PlusCircle, ListChecks, Clock, CheckCircle2, BarChart3, LogOut, ExternalLink, Eye, Archive } from "lucide-react";
import Logo from "@/components/Logo";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { adminSettings, updateAdminSettings, apiError } from "@/lib/adminApi";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/picks/new", label: "Create Pick", icon: PlusCircle, testid: "admin-nav-create" },
  { to: "/admin/picks", label: "Manage Picks", icon: ListChecks, end: true, testid: "admin-nav-manage" },
  { to: "/admin/picks/pending", label: "Pending Picks", icon: Clock, testid: "admin-nav-pending" },
  { to: "/admin/picks/settled", label: "Settled Picks", icon: CheckCircle2, testid: "admin-nav-settled" },
  { to: "/admin/results", label: "Results Overview", icon: BarChart3, testid: "admin-nav-results" },
  { to: "/admin/historical", label: "Historical Slips", icon: Archive, testid: "admin-nav-historical" },
];

function ModeToggle({ label, icon: Icon, value, options, onChange, testid }) {
  return (
    <div className="rounded-lg border border-[#1E2738] bg-[#0F131D] p-3">
      <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"><Icon size={11} /> {label}</p>
      <div className="grid grid-cols-2 gap-1">
        {options.map((o) => (
          <button
            key={o.value}
            data-testid={`${testid}-${o.value}`}
            onClick={() => onChange(o.value)}
            className={`rounded-md px-2 py-1.5 font-display text-[11px] font-bold uppercase tracking-wider transition-colors ${
              value === o.value ? "bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/40" : "border border-transparent text-slate-400 hover:text-white"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function AdminSettingsPanel() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: adminSettings });
  const mut = useMutation({
    mutationFn: updateAdminSettings,
    onSuccess: (d) => {
      qc.setQueryData(["admin-settings"], d);
      qc.invalidateQueries();
      toast.success(d.vip_test_mode === "vip" ? "Previewing as a VIP member — your browser now sees unlocked VIP content" : "Previewing as a non-member — VIP content is locked for you");
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const vip = data?.vip_test_mode === "vip";
  return (
    <div className="space-y-3">
      <ModeToggle
        label="VIP Test Mode" icon={Eye} testid="admin-vip-mode"
        value={data?.vip_test_mode}
        options={[{ value: "guest", label: "Non-member" }, { value: "vip", label: "VIP" }]}
        onChange={(v) => mut.mutate({ vip_test_mode: v })}
      />
      <div className={`rounded-lg border px-3 py-2 font-mono text-[10px] leading-relaxed ${vip ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#1E2738] bg-[#0F131D] text-slate-500"}`} data-testid="admin-vip-mode-status">
        {mut.isPending ? "Switching…" : vip ? "ACTIVE: VIP PREVIEW — only your admin browser is affected." : "ACTIVE: NON-MEMBER PREVIEW — you see what a free visitor sees."}
        <Link to="/picks" target="_blank" className="mt-1 block underline-offset-2 hover:underline" data-testid="admin-preview-picks-link">Open /picks preview ↗</Link>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();

  if (admin === null) return <div className="flex min-h-screen items-center justify-center bg-[#080A0F] font-mono text-xs text-slate-500">Checking session...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const cls = ({ isActive }) =>
    `flex items-center gap-2.5 rounded-lg px-3 py-2.5 font-display text-sm font-semibold uppercase tracking-wider transition-colors ${
      isActive ? "bg-[#00E676]/10 text-[#00E676]" : "text-slate-400 hover:bg-[#141A26] hover:text-white"
    }`;

  return (
    <div className="flex min-h-screen bg-[#080A0F]" data-testid="admin-layout">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-[#1E2738] bg-[#0A0D14] p-5 lg:flex">
        <div className="flex items-center justify-between">
          <Logo />
        </div>
        <span className="mt-3 tag w-fit border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">Admin</span>
        <nav className="mt-8 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={cls} data-testid={n.testid}>
              <n.icon size={15} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-8"><AdminSettingsPanel /></div>
        <div className="mt-auto space-y-2 pt-8">
          <Link to="/picks" target="_blank" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-500 hover:text-[#00E676]" data-testid="admin-view-site-link">
            <ExternalLink size={12} /> View public site
          </Link>
          <p className="truncate font-mono text-[11px] text-slate-500">{admin.username}</p>
          {admin.expires_at && (
            <p className="font-mono text-[10px] text-slate-600" data-testid="admin-session-expiry">Session expires {new Date(admin.expires_at).toLocaleString()}</p>
          )}
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-slate-400 hover:text-[#EF4444]">
            <LogOut size={12} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 overflow-x-auto border-b border-[#1E2738] bg-[#0A0D14] px-4 py-3 lg:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={cls}>{n.label}</NavLink>
          ))}
          <button onClick={logout} className="ml-auto font-mono text-[11px] uppercase text-slate-400">Sign out</button>
        </div>
        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-8 sm:py-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
